<template>
  <div class="mb-2 mt-1 rounded-2xl bg-background-dark p-4">
    <div class="flex border-b border-border-inverted pb-4">
      <div class="flex w-1/2 items-baseline justify-center p-2 pr-0">
        <p class="text-base">{{ t['w.module.product_hero.sum']() }}:</p>
        <span class="font-semibold md:text-lg">&nbsp;{{ totalSum }} {{ t['w.global.currency-symbol']() }}</span>
      </div>
      <div class="flex w-1/2 items-baseline justify-center p-2 pr-0">
        <p class="text-base">{{ t['w.module.product_hero.monthly_pay']() }}:</p>
        <span class="font-semibold md:text-lg">&nbsp;{{ monthlySum }} {{ t['w.global.currency-symbol']() }}</span>
      </div>
    </div>
    <div class="py-4">
      <label class="mb-4 block text-center" for="productPayPeriod">
        {{ t['w.menu.jm.periood']() }}:
        <span class="text-lg font-semibold">{{ selectedDuration }}&nbsp;{{ t['w.menu.jm.kuud']() }}</span>
      </label>
      <div id="productPayPeriod" class="-m-1 mx-auto flex max-w-72 flex-wrap justify-center">
        <label v-for="duration of durationChoices" :key="`duration-${duration}`" class="p-1">
          <input v-model="selectedDuration" class="hidden" type="radio" name="paymentperiod" :value="duration" @change="onChangeSelect()" />
          <span
            class="flex size-10 items-center justify-center rounded border-2 bg-background text-base font-semibold hover:border-border-hover"
            :class="[selectedDuration === duration ? 'border-border' : 'border-border-inverted']"
          >
            {{ duration }}
          </span>
        </label>
      </div>
      <label class="mb-4 mt-8 block text-center" for="productFirstPayPercent">
        {{ t['w.menu.jm.sissemaks']() }}:
        <span class="text-lg font-semibold">{{ selectedUpfrontPercentage }}% / {{ stringFirstPay }} {{ useCurrencySymbol() }}</span>
      </label>
      <div id="productFirstPayPercent" class="-m-1 mx-auto flex max-w-72 flex-wrap justify-center">
        <label v-for="percentage of upfrontPercentages" :key="`percentage-${percentage}`" class="p-1">
          <input v-model="selectedUpfrontPercentage" class="hidden" type="radio" name="advancepaymentPercent" :value="percentage" @change="onChangeSelect()" />
          <span
            class="flex size-10 items-center justify-center rounded border-2 bg-background text-base font-semibold hover:border-border-hover"
            :class="[selectedUpfrontPercentage === percentage ? 'border-border' : 'border-border-inverted']"
          >
            {{ percentage }}%
          </span>
        </label>
      </div>
    </div>
    <p v-if="!user" class="border-t border-border-inverted pt-4 text-center text-xs" v-html="t['w.toode.calculatorbody']()" />
  </div>
</template>

<script setup lang="ts">
import * as t from '~/messages'
import { useCurrencySymbol } from '~/composables/price'

const UPFRONT_PERCENTAGES = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70]
const DEFAULT_UPFRONT_PERCENTAGE = 5
const DURATION_CHOICES = [3, 6, 12, 18, 24, 30, 36, 42, 48]
const DEFAULT_DURATION = 24

const user = useUserState()

const props = defineProps<{ sum: number }>()

const upfrontPercentages = ref(UPFRONT_PERCENTAGES)
const selectedUpfrontPercentage = ref(DEFAULT_UPFRONT_PERCENTAGE)
const durationChoices = ref(DURATION_CHOICES)
const selectedDuration = ref(DEFAULT_DURATION)

const numericIntress = computed(() => {
  return 0.229
})

const numericFirstPay = computed(() => {
  return numericSissemaksOfPercentage(selectedUpfrontPercentage.value)
})

const stringFirstPay = computed(() => {
  return numericSissemaksOfPercentage(selectedUpfrontPercentage.value).toFixed(2).replace('.', ',')
})

const summa = computed(() => {
  return (props.sum / 100000).toFixed(2).toString().replace('.', ',')
})

const totalSum = computed(() => {
  return (selectedDuration.value * numericMonthlyPay()).toFixed(2).replace('.', ',')
})

const monthlySum = computed(() => {
  return numericMonthlyPay().toFixed(2).replace('.', ',')
})

const numericSissemaksOfPercentage = (percentage) => {
  return Math.round(numericSum() * percentage) / 100
}

const numericMonthlyPayOfSissemaks = (numericFirstPay) => {
  if (numericIntress.value === 0) {
    return (numericSum() - numericFirstPay) / selectedDuration.value
  }

  return ((numericIntress.value / 12.0) * (numericSum() - numericFirstPay)) / (1.0 - 1.0 / Math.pow(numericIntress.value / 12.0 + 1.0, selectedDuration.value))
}

const numericMonthlyPay = () => {
  return numericMonthlyPayOfSissemaks(numericFirstPay.value)
}

const numericSum = () => {
  const val = parseFloat(summa.value.replace(',', '.').replace(' ', ''), 10)

  return isNaN(val) ? 0 : val
}

const emit = defineEmits<{
  kuumaks: [amount: number]
}>()

const onChangeSelect = () => {
  emit('kuumaks', numericMonthlyPay() * 100000)
}

watch(
  () => props.sum,
  () => {
    onChangeSelect()
  },
  {
    immediate: true,
  }
)
</script>
